import React, { useState, useEffect } from 'react';
import { Menu, X, Droplets, Trophy, Award, Navigation, Camera, Gauge, Cpu, Users, Mail, Phone, MapPin, Linkedin, Github, ExternalLink, ChevronDown, Image, ChevronLeft, ChevronRight } from 'lucide-react';

const UnderwaterRobotShowcase = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Height of fixed navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Home', id: 'home' },
    { name: 'Project', id: 'project' },
    { name: 'Gallery', id: 'gallery' },
    { name: 'Competitions', id: 'competitions' },
    { name: 'Team', id: 'team' },
    { name: 'Contact', id: 'contact' }
  ];

  const features = [
    { icon: Navigation, title: 'Visual-Inertial Odometry', desc: 'Proven VIO system for accurate underwater positioning' },
    { icon: Camera, title: 'OAK-D-W-97 Stereo Camera', desc: 'Advanced stereo vision for depth perception' },
    { icon: Gauge, title: 'Modular Architecture', desc: 'Supports rapid iteration and component upgrades' },
    { icon: Cpu, title: 'Synthetic Data Training', desc: 'Computer vision using synthetic data environments' }
  ];

  const competitions = [
    { 
      name: 'SAUVC 2025', 
      place: 'Qualified for Finals', 
      date: 'March 2025', 
      highlight: 'Task 1 Completed',
      description: 'Successfully qualified for finals and completed task 1 during practice sessions'
    },
    { 
      name: 'RoboSub 2025', 
      place: 'Upcoming', 
      date: 'July-August 2025', 
      highlight: 'California',
      description: 'Preparing with improved synthetic data CV and reliability enhancements'
    }
  ];

  const teamMembers = [
    {
      name: 'Pranav Garg',
      role: 'Team Lead',
      image: '/images/team/pranav.jpg',
      bio: 'Overall project coordination and strategic planning',
      linkedin: 'https://linkedin.com',
      github: 'https://github.com'
    },
    {
      name: 'John Henry (THE GOD)',
      role: 'Ex Team Lead',
      image: '/images/team/god.jpg',
      bio: 'Everything',
      github: 'https://github.com'
    },
    {
      name: 'Suheera Banu',
      role: 'Hardware Lead',
      image: '/images/team/suheera.jpg',
      bio: 'Leads mechanical design and hardware integration',
      linkedin: 'https://linkedin.com'
    },
    {
      name: 'Zhao',
      role: 'Software Lead',
      image: '/images/team/zhao.jpg',
      bio: 'Oversees software architecture and algorithm development',
      github: 'https://github.com'
    },
    {
      name: 'Xin Yang',
      role: 'Electronics Lead',
      image: '/images/team/xinyang.jpg',
      bio: 'Circuit design and electronics integration specialist',
      linkedin: 'https://linkedin.com'
    },
    {
      name: 'Vanika Sharma',
      role: 'Hardware Engineer',
      image: '/images/team/vanika.jpg',
      bio: 'Mechanical systems and structural design',
      linkedin: 'https://linkedin.com'
    },
    {
      name: 'Wen Xuan Lee',
      role: 'Hardware Engineer',
      image: '/images/team/wenxuan.jpg',
      bio: 'CAD design and fabrication specialist',
      github: 'https://github.com'
    },
    {
      name: 'Nicholas Lukman',
      role: 'Hardware Engineer',
      image: '/images/team/nicholas.jpg',
      bio: 'Testing and waterproofing systems',
      linkedin: 'https://linkedin.com'
    },
    {
      name: 'Oviya Ravish Kumar',
      role: 'Hardware Engineer',
      image: '/images/team/oviya.jpg',
      bio: 'Materials and structural analysis',
      linkedin: 'https://linkedin.com'
    },
    {
      name: 'Nit',
      role: 'Software Engineer',
      image: '/images/team/nit.jpg',
      bio: 'Computer vision and perception systems',
      github: 'https://github.com'
    },
    {
      name: 'Viet Minh',
      role: 'Software Engineer',
      image: '/images/team/vietminh.jpg',
      bio: 'Control systems and navigation algorithms',
      github: 'https://github.com'
    },
    {
      name: 'Somesh',
      role: 'Software Engineer',
      image: '/images/team/somesh.jpg',
      bio: 'Simulation and testing frameworks',
      github: 'https://github.com'
    }
  ];

  const contactInfo = {
    email: 'sutdunderwaterunit@gmail.com',
    phone: '+65 88052759',
    address: 'IDER Building 1 Level 5 SUTD, Singapore',
    socialMedia: {
      github: 'https://github.com/underwater-robotics',
      linkedin: 'https://linkedin.com/company/underwater-robotics',
      youtube: 'https://youtube.com/@underwater-robotics'
    }
  };

  // Gallery images - replace with your actual image paths
  const galleryImages = [
    {
      id: 1,
      src: '/images/gallery/robot-poolside.jpg',
      title: 'UWU at Poolside',
      description: 'Robot ready for pool testing'
    },
    {
      id: 2,
      src: '/images/gallery/sauvc-team.jpg',
      title: 'SAUVC 2025 Team',
      description: 'Team photo at SAUVC competition'
    },
    {
      id: 3,
      src: '/images/gallery/pool-testing.jpg',
      title: 'Pool Testing',
      description: 'Testing depth control and navigation'
    },
    {
      id: 4,
      src: '/images/gallery/hardware-assembly.jpg',
      title: 'Hardware Assembly',
      description: 'Working on ABS parts and aluminum endcap'
    },
    {
      id: 5,
      src: '/images/gallery/simulator.jpg',
      title: 'Competition Simulator',
      description: 'Testing in developed simulation environment'
    },
    {
      id: 6,
      src: '/images/gallery/vision-testing.jpg',
      title: 'Computer Vision Testing',
      description: 'Synthetic data environment for CV training'
    }
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900">
      {/* Navigation */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-2">
              {/* Logo */}
              <img 
                src="/images/underwater-unit-logo.png" 
                alt="Underwater Unit Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              {/* Fallback icon if logo doesn't load */}
              <div className="hidden items-center space-x-2">
                <Droplets className="w-8 h-8 text-cyan-400" />
                <h1 className="text-2xl font-bold text-white">Underwater Unit</h1>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-gray-300 hover:text-cyan-400 transition-colors font-medium"
                >
                  {item.name}
                </button>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t border-slate-700">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left py-2 text-gray-300 hover:text-cyan-400 transition-colors"
                >
                  {item.name}
                </button>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="container mx-auto text-center">
          <div className="mb-8">
            {/* Main Logo */}
            <img 
              src="/images/underwater-unit-logo.png" 
              alt="Underwater Unit" 
              className="w-64 h-auto mx-auto mb-8"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            {/* Fallback animated icon */}
            <div className="hidden">
              <div className="w-40 h-40 mx-auto bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center shadow-2xl shadow-cyan-500/50 animate-pulse">
                <Droplets className="w-20 h-20 text-white" />
              </div>
            </div>
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6">
            UWU
          </h2>
          <p className="text-2xl md:text-3xl text-gray-300 mb-4">
            (UnderWATER UNIT)
          </p>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Autonomous underwater robot designed as a modular research and competition platform
            for RoboSub and SAUVC
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <button 
              onClick={() => scrollToSection('project')}
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 rounded-full font-semibold transition-all transform hover:scale-105 shadow-lg shadow-cyan-500/30"
            >
              Explore Project
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="border border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900 px-8 py-3 rounded-full font-semibold transition-all"
            >
              Get in Touch
            </button>
          </div>
          <div className="mt-16 animate-bounce">
            <ChevronDown className="w-8 h-8 text-cyan-400 mx-auto" />
          </div>
        </div>
      </section>

      {/* Project Section */}
      <section id="project" className="py-20 px-6">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">Project Overview</h2>
          
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-cyan-400 mb-4">About UWU</h3>
              <p className="text-gray-300 mb-4">
                Underwater Unit (UWU) is an autonomous underwater robot developed as a modular research 
                and competition platform by our robotics team. Designed for events such as RoboSub and 
                SAUVC (Singapore AUV Challenge), UWU serves as both a testbed for innovation and a 
                training tool for developing real-world marine robotics capabilities.
              </p>
              <p className="text-gray-300 mb-4">
                Equipped with a vision-based perception system, an inertial measurement unit (IMU), 
                and depth sensors, UWU can perform core tasks including underwater navigation, path 
                following and depth control. Its modular architecture supports rapid iteration and 
                future upgrades.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  <span className="text-gray-300">Multiple competition wins</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Award className="w-5 h-5 text-yellow-400" />
                  <span className="text-gray-300">Innovation awards recipient</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-cyan-400 mb-4">Key Achievements</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">Hardware Improvements</h4>
                  <ul className="text-gray-300 text-sm space-y-1 ml-4">
                    <li>• Replaced PLA parts with ABS to prevent cracking</li>
                    <li>• Upgraded to aluminum endcap for durability</li>
                    <li>• Integrated OAK-D-W-97 stereo camera</li>
                    <li>• Fixed depth sensor issues</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">Software Achievements</h4>
                  <ul className="text-gray-300 text-sm space-y-1 ml-4">
                    <li>• Developed competition simulator</li>
                    <li>• Proved Visual-Inertial Odometry in sim and real life</li>
                    <li>• Created notebook-compatible robot API</li>
                    <li>• Built synthetic data environment for CV</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">Future Development</h4>
                  <ul className="text-gray-300 text-sm space-y-1 ml-4">
                    <li>• Pixhawk integration planned</li>
                    <li>• Cooling system implementation</li>
                    <li>• Enhanced power delivery system</li>
                    <li>• RoboSub strategy development</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <h3 className="text-3xl font-semibold text-white text-center mb-12">Core Features</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-800/70 transition-all transform hover:scale-105">
                <div className="bg-gradient-to-br from-cyan-400 to-blue-600 p-3 rounded-lg w-fit mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">{feature.title}</h4>
                <p className="text-gray-300 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-6 bg-slate-900/50">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">Project Gallery</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {galleryImages.map((image, idx) => (
              <div 
                key={image.id}
                onClick={() => {
                  setCurrentImageIndex(idx);
                  setIsGalleryModalOpen(true);
                }}
                className="bg-slate-800/50 backdrop-blur-sm rounded-xl overflow-hidden hover:bg-slate-800/70 transition-all transform hover:scale-105 cursor-pointer group"
              >
                <div className="aspect-video bg-gradient-to-br from-slate-700 to-slate-800 relative overflow-hidden">
                  {/* Placeholder for image */}
                  {/* <div className="absolute inset-0 flex items-center justify-center">
                    <Image className="w-16 h-16 text-gray-500" />
                  </div> */}
                  {/* Actual image - uncomment when you have images */}
                  <img 
                    src={image.src} 
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-semibold">
                      View Larger
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-white mb-1">{image.title}</h3>
                  <p className="text-gray-300 text-sm">{image.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Gallery Modal */}
          {isGalleryModalOpen && (
            <div 
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              onClick={() => setIsGalleryModalOpen(false)}
            >
              <div 
                className="max-w-6xl w-full relative"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button 
                  onClick={() => setIsGalleryModalOpen(false)}
                  className="absolute top-4 right-4 text-white hover:text-cyan-400 transition-colors z-10"
                >
                  <X className="w-8 h-8" />
                </button>

                {/* Navigation buttons */}
                <button
                  onClick={() => setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-cyan-400 transition-colors bg-black/50 p-2 rounded-full"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={() => setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-cyan-400 transition-colors bg-black/50 p-2 rounded-full"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>

                {/* Image display */}
                <div className="bg-slate-800 rounded-xl overflow-hidden">
                  <div className="aspect-video bg-gradient-to-br from-slate-700 to-slate-800 relative">
                    {/* Placeholder */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Image className="w-24 h-24 text-gray-500" />
                    </div>
                    {/* Actual image - uncomment when you have images */}
                    {/* <img 
                      src={galleryImages[currentImageIndex].src} 
                      alt={galleryImages[currentImageIndex].title}
                      className="w-full h-full object-contain"
                    /> */}
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-semibold text-white mb-2">
                      {galleryImages[currentImageIndex].title}
                    </h3>
                    <p className="text-gray-300">
                      {galleryImages[currentImageIndex].description}
                    </p>
                    <p className="text-sm text-gray-400 mt-4">
                      Image {currentImageIndex + 1} of {galleryImages.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Competitions Section */}
      <section id="competitions" className="py-20 px-6 bg-slate-900/50">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">Competition Achievements</h2>
          
          <div className="space-y-8 max-w-4xl mx-auto">
            {competitions.map((comp, idx) => (
              <div key={idx} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 hover:bg-slate-800/70 transition-all">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-semibold text-white mb-2">{comp.name}</h3>
                    <p className="text-gray-400">{comp.date}</p>
                  </div>
                  <div className="mt-4 md:mt-0 flex items-center gap-4">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-slate-900 px-4 py-2 rounded-full font-bold">
                      {comp.place}
                    </span>
                    {comp.highlight && (
                      <span className="text-cyan-400 flex items-center">
                        <Award className="w-5 h-5 mr-2" />
                        {comp.highlight}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-gray-300">{comp.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex flex-col items-center justify-center p-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl">
              <div className="bg-slate-900 px-8 py-6 rounded-2xl">
                <p className="text-2xl text-white font-bold mb-4">Project Timeline FY24-25</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
                  <div className="text-center">
                    <p className="text-cyan-400 font-semibold">OCT-NOV '24</p>
                    <p className="text-gray-300 text-sm">Training Stage</p>
                  </div>
                  <div className="text-center">
                    <p className="text-cyan-400 font-semibold">NOV-MAR '25</p>
                    <p className="text-gray-300 text-sm">Refining Stage</p>
                  </div>
                  <div className="text-center">
                    <p className="text-cyan-400 font-semibold">MAR-JUL '25</p>
                    <p className="text-gray-300 text-sm">Development</p>
                  </div>
                  <div className="text-center">
                    <p className="text-cyan-400 font-semibold">JUL-AUG '25</p>
                    <p className="text-gray-300 text-sm">Competition</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 px-6">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">Meet Our Team</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, idx) => (
              <div key={idx} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 hover:bg-slate-800/70 transition-all transform hover:scale-105">
                <div className="text-center">
                  {/* Profile Image Placeholder */}
                  <div className="w-32 h-32 mx-auto mb-4 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center">
                    {member.image ? (
                      <img src={member.image} alt={""} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <Users className="w-16 h-16 text-white" />
                    )}
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-1">{member.name}</h3>
                  <p className="text-cyan-400 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-300 text-sm mb-4">{member.bio}</p>
                  
                  <div className="flex justify-center gap-3">
                    {member.linkedin && (
                      <a href={member.linkedin} target="_blank" rel="noopener noreferrer" 
                         className="text-gray-400 hover:text-cyan-400 transition-colors">
                        <Linkedin className="w-5 h-5" />
                      </a>
                    )}
                    {member.github && (
                      <a href={member.github} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-cyan-400 transition-colors">
                        <Github className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-slate-900/50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">Get In Touch</h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-cyan-400 mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 text-cyan-400 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Email</p>
                    <a href={`mailto:${contactInfo.email}`} className="text-gray-300 hover:text-cyan-400 transition-colors">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <Phone className="w-6 h-6 text-cyan-400 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Phone</p>
                    <a href={`tel:${contactInfo.phone}`} className="text-gray-300 hover:text-cyan-400 transition-colors">
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-cyan-400 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Address</p>
                    <p className="text-gray-300 whitespace-pre-line">{contactInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-700">
                <p className="text-white font-semibold mb-4">Follow Us</p>
                <div className="flex space-x-4">
                  <a href={contactInfo.socialMedia.github} target="_blank" rel="noopener noreferrer"
                     className="text-gray-400 hover:text-cyan-400 transition-colors">
                    <Github className="w-6 h-6" />
                  </a>
                  <a href={contactInfo.socialMedia.linkedin} target="_blank" rel="noopener noreferrer"
                     className="text-gray-400 hover:text-cyan-400 transition-colors">
                    <Linkedin className="w-6 h-6" />
                  </a>
                  <a href={contactInfo.socialMedia.youtube} target="_blank" rel="noopener noreferrer"
                     className="text-gray-400 hover:text-cyan-400 transition-colors">
                    <ExternalLink className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-cyan-400 mb-6">Send Us a Message</h3>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-white mb-2">Name</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors"
                    placeholder="Your Name"
                  />
                </div>
                
                <div>
                  <label className="block text-white mb-2">Email</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-white mb-2">Message</label>
                  <textarea 
                    rows="4"
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                    placeholder="Your message..."
                  />
                </div>
                
                <button 
                  type="submit"
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-3 rounded-lg font-semibold transition-all transform hover:scale-105"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 border-t border-slate-800">
        <div className="container mx-auto text-center">
          <p className="text-gray-400">
            © 2025 SOAR all rights reserved. 
          </p>
        </div>
      </footer>
    </div>
  );
};

export default UnderwaterRobotShowcase;